import {messages} from '@smartmixin/i18n';
import {MessageDescriptor} from 'react-intl';
import {
  ForecastMapContourLevelsMode,
  ForecastMapLayerType,
  ForecastMapNanPolicy,
  ForecastMapProjection,
  ForecastMapUnitConversion,
} from '../../api/types';

type FormatMessage = (descriptor: MessageDescriptor) => string;

const LAYER_TYPE_MESSAGES: Record<ForecastMapLayerType, MessageDescriptor> = {
  filled_contour: messages.forecastMaps.layerTypeFilledContour,
  contour: messages.forecastMaps.layerTypeContour,
  raster: messages.forecastMaps.layerTypeRaster,
  barbs: messages.forecastMaps.layerTypeBarbs,
  vector_field: messages.forecastMaps.layerTypeVectorField,
  magnitude: messages.forecastMaps.layerTypeMagnitude,
};

// These identifiers come from the SERVER capabilities payload, which can
// grow ahead of this client's typed unions: an unmapped value must degrade
// to the raw identifier, never reach formatMessage(undefined) — that throws
// and would take the whole builder screen down with it.
const labelOrRaw = (
  descriptor: MessageDescriptor | undefined,
  raw: string,
  formatMessage: FormatMessage,
): string => (descriptor ? formatMessage(descriptor) : raw);

export const layerTypeLabel = (
  layerType: ForecastMapLayerType,
  formatMessage: FormatMessage,
): string =>
  labelOrRaw(LAYER_TYPE_MESSAGES[layerType], layerType, formatMessage);

const PROJECTION_MESSAGES: Record<ForecastMapProjection, MessageDescriptor> = {
  plate_carree: messages.forecastMaps.projectionPlateCarree,
  mercator: messages.forecastMaps.projectionMercator,
  lambert_conformal: messages.forecastMaps.projectionLambertConformal,
  stereographic: messages.forecastMaps.projectionStereographic,
};

export const projectionLabel = (
  projection: ForecastMapProjection,
  formatMessage: FormatMessage,
): string =>
  labelOrRaw(PROJECTION_MESSAGES[projection], projection, formatMessage);

const NAN_POLICY_MESSAGES: Record<ForecastMapNanPolicy, MessageDescriptor> = {
  skip: messages.forecastMaps.nanSkip,
  mask: messages.forecastMaps.nanMask,
  clip_to_min: messages.forecastMaps.nanClipToMin,
};

export const nanPolicyLabel = (
  policy: ForecastMapNanPolicy,
  formatMessage: FormatMessage,
): string => labelOrRaw(NAN_POLICY_MESSAGES[policy], policy, formatMessage);

// Unit conversions are symbolic (identical across locales) except "none".
const UNIT_CONVERSION_LABELS: Record<
  Exclude<ForecastMapUnitConversion, 'none'>,
  string
> = {
  kelvin_to_celsius: 'K → °C',
  ms_to_kmh: 'm/s → km/h',
  ms_to_mph: 'm/s → mph',
  pa_to_hpa: 'Pa → hPa',
  mm_to_in: 'mm → in',
};

export const unitConversionLabel = (
  unit: ForecastMapUnitConversion,
  formatMessage: FormatMessage,
): string =>
  unit === 'none'
    ? formatMessage(messages.forecastMaps.unitNone)
    : UNIT_CONVERSION_LABELS[unit];

const LEVELS_MODE_MESSAGES: Record<
  ForecastMapContourLevelsMode,
  MessageDescriptor
> = {
  linspace: messages.forecastMaps.levelsModeLinspace,
  arange: messages.forecastMaps.levelsModeArange,
  explicit: messages.forecastMaps.levelsModeExplicit,
};

export const levelsModeLabel = (
  mode: ForecastMapContourLevelsMode,
  formatMessage: FormatMessage,
): string => formatMessage(LEVELS_MODE_MESSAGES[mode]);
